<template>
  <div id="listWidget" class="mb-5">
    <InstructionStepsCard
      title="등록 단계"
      :steps="registSteps"
    />

    <div class="action-button-container mb-3">
      <button
        type="button"
        class="btn btn-primary w-100"
        :disabled="registering"
        @click="onQRCodeClick"
      >
        <i class="fa-solid fa-qrcode me-2"></i>
        QR 코드 촬영
      </button>
    </div>

    <div class="input-card card-item mb-3">
      <div class="input-card-body">
        <input
          type="text"
          class="form-control form-control-lg fs-15px"
          placeholder="시리얼을 입력해주세요. 예: M24_885721b28ddc"
          v-model="serialInput"
          :disabled="registering"
          autocapitalize="off"
          autocomplete="off"
          spellcheck="false"
        />
      </div>
    </div>

    <div class="text-muted fs-12px mb-2"><b>영역 선택</b></div>
    <div class="list-group mb-3" v-if="zones.length > 0">
      <ListGroupItem
        v-for="zone in zones"
        :key="zone._id"
        class="card-item mb-2"
        style="border-radius: 1.2rem; border-top: none !important;"
      >
        <div class="form-check mb-0">
          <input
            class="form-check-input"
            type="checkbox"
            :id="`zone-${zone._id}`"
            :value="zone._id"
            :checked="String(selectedZoneId) === String(zone._id)"
            :disabled="registering"
            @change="onSelectZoneChange"
          />
          <label class="form-check-label" :for="`zone-${zone._id}`">
            {{ zone.name }}
          </label>
        </div>
      </ListGroupItem>
    </div>
    <p v-else class="text-muted fs-13px mb-3">
      등록할 영역이 없습니다. 영역을 먼저 추가해주세요.
    </p>

    <button
      type="button"
      class="btn btn-primary w-100"
      :disabled="registering || zones.length === 0"
      @click="onRegistClick"
    >
      등록
    </button>
  </div>
</template>

<script setup>
import {
  ref,
  getCurrentInstance,
  onMounted,
  onBeforeMount,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Axios, { ZoneApi } from '@/api';
import useMobile from '@/common/hooks/useMobile';
import useMqttControl from '@/common/hooks/useMqttControl';
import ListGroupItem from '@/common/components/widgets/ListGroupItem.vue';
import InstructionStepsCard from './components/InstructionStepsCard.vue';
import { SettingConfig } from '@/common/constants';

const registSteps = [
  '<strong>와이파이 변경</strong>에서 조명제어기 와이파이를 먼저 연결하십시오.',
  '제품 <strong>QR코드</strong>를 촬영하거나 시리얼을 입력하십시오.',
  '등록할 <strong>영역</strong>을 선택한 뒤 등록하십시오.',
];

const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const store = useStore();
const router = useRouter();
const { readQRCode } = useMobile();
const mqttControl = useMqttControl();

const serialInput = ref('');
const zones = ref([]);
const selectedZoneId = ref('');
const registering = ref(false);
let alive = true;

const normalizeLightSerial = (raw) => {
  const text = String(raw || '').trim();
  const matched = text.match(/M\d{2}_[0-9a-fA-F]{12}/i);
  if (!matched) {
    return '';
  }
  const [model, mac] = matched[0].split('_');
  return `${model.toUpperCase()}_${mac.toLowerCase()}`;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '조명제어기 QR코드로 등록',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });
});

onBeforeUnmount(() => {
  alive = false;
  emitter.off('onReadQRCodeResult');
  emitter.emit('hideLoading');
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
});

onMounted(() => {
  requestZones();
});

const onSelectZoneChange = (e) => {
  selectedZoneId.value = e.target.checked ? e.target.value : '';
};

const requestZones = async () => {
  try {
    const result = await Axios.get(ZoneApi.ZONES, { isOwner: true });
    if (!alive) {
      return;
    }
    if (result.resultCode !== '0000') {
      emitter.emit('showPopAlert', {
        description: result.resultMessage || '영역 목록을 불러오지 못했습니다.',
      });
      return;
    }
    const list = Array.isArray(result.resultData) ? result.resultData : [];
    zones.value = list;
    const currentZone = store.getters['home/getCurrentZone'];
    const currentId = currentZone?._id;
    const matched = list.find((zone) => String(zone._id) === String(currentId));
    if (matched) {
      selectedZoneId.value = matched._id;
    } else if (list.length === 1) {
      selectedZoneId.value = list[0]._id;
    }
  } catch (e) {
    console.log(e);
    if (alive) {
      emitter.emit('showPopAlert', {
        description: '영역 목록을 불러오지 못했습니다.',
      });
    }
  }
};

const onQRCodeClick = async () => {
  if (registering.value) {
    return;
  }
  try {
    const result = await readQRCode();
    if (!alive) {
      return;
    }
    const serial = normalizeLightSerial(result);
    if (!serial) {
      emitter.emit('showPopAlert', {
        description: '조명제어기 시리얼 QR이 아닙니다.\n다시 촬영해주세요.',
      });
      return;
    }
    serialInput.value = serial;
  } catch (e) {
    console.log(e);
    if (alive) {
      emitter.emit('showPopAlert', {
        description: 'QR코드 인식에 실패했습니다.\n다시 시도해주세요.',
      });
    }
  }
};

const askBoardVersion = (serial) => {
  mqttControl.onPublishController(serial, {
    serial,
    cmd: 'q',
    type: 1,
  });
};

const requestRegister = async (zoneId, serial) => {
  const model = serial.split('_')[0];
  let last = null;
  for (let i = 0; i < SettingConfig.requestCount; i++) {
    if (!alive) {
      return null;
    }
    askBoardVersion(serial);
    emitter.emit(
      'setLoadingText',
      i === 0
        ? '조명제어기 정보를 서버에 등록중입니다.'
        : `조명제어기 정보를 서버에 ${i + 1}번째 등록 시도하고 있습니다.`,
    );
    if (i === 0) {
      await sleep(2000);
      if (!alive) {
        return null;
      }
    }
    try {
      last = await Axios.post(
        ZoneApi.REGIST_ZONE_CTRLS.replace(':id', zoneId),
        { _id: serial, model },
      );
    } catch (e) {
      last = e;
    }
    if (last?.resultCode === '0000' || last?.resultCode === '1001') {
      return last;
    }
    if (i < SettingConfig.requestCount - 1) {
      await sleep(SettingConfig.requestInterval);
    }
  }
  return last;
};

const onRegistClick = async () => {
  const serial = normalizeLightSerial(serialInput.value);
  if (!serial) {
    emitter.emit('showPopAlert', {
      description: serialInput.value.trim().length === 0
        ? 'QR코드를 인식하거나\n시리얼을 입력해주세요.'
        : '조명제어기 시리얼 형식이 아닙니다.\n예: M24_885721b28ddc',
    });
    return;
  }
  serialInput.value = serial;

  if (!selectedZoneId.value) {
    emitter.emit('showPopAlert', {
      description: '등록할 영역을 선택해주세요.',
    });
    return;
  }

  registering.value = true;
  emitter.emit('showLoading');
  try {
    const result = await requestRegister(selectedZoneId.value, serial);
    if (!alive) {
      return;
    }
    if (result?.resultCode === '0000') {
      try {
        await store.dispatch('home/fetchAllZones');
      } catch (e) {
        console.log(e);
      }
      emitter.emit('hideLoading');
      emitter.emit('showPopAlert', {
        description: '조명제어기가 등록되었습니다.',
        okCallback: () => router.back(),
      });
      return;
    }
    if (result?.resultCode === '1001') {
      emitter.emit('hideLoading');
      emitter.emit('showPopAlert', {
        description: result.resultMessage || '이미 등록된 컨트롤러입니다.\n이관받기를 시도해주세요.',
      });
      return;
    }
    emitter.emit('hideLoading');
    emitter.emit('showPopAlert', {
      description: '와이파이에 연결된 조명제어기만 등록할 수 있습니다.\n와이파이 변경을 먼저 완료한 뒤 다시 시도해주세요.',
    });
  } catch (e) {
    console.log(e);
    if (alive) {
      emitter.emit('hideLoading');
      emitter.emit('showPopAlert', {
        description: '조명제어기 등록에 실패했습니다.',
      });
    }
  } finally {
    registering.value = false;
  }
};
</script>

<style scoped>
.action-button-container .btn,
.btn.w-100 {
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
}

.input-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.input-card-body {
  padding: 0.75rem 1rem;
}
</style>
